/* global customElements, HTMLElement */
/**
 * <product-reviews> custom element
 *
 * Fetches approved reviews from the BlankSlab Manager public API and renders them.
 * Spec: REVIEWS-BACKEND-SPEC.md §5.1.C
 *
 * The Shopify numeric product.id is sent as `product_ref`. It is immutable and
 * resolved server-side via ExternalProductMapping to the internal BaseVariant.
 */

const PR_TAG = '[product-reviews]';

class ProductReviews extends HTMLElement {
  constructor() {
    super();
    this.state = {
      page: 1,
      limit: 0,
      total: 0,
      loaded: 0,
      filter: 'all',
      loading: false,
    };
  }

  connectedCallback() {
    console.log(`${PR_TAG} connectedCallback — element mounted in DOM`);

    this.apiBase = this.dataset.apiBase || '';
    this.channel = this.dataset.channel || '';
    this.productRef = this.dataset.productRef || '';
    this.state.limit = parseInt(this.dataset.initialLimit, 10) || 6;
    this.loadMoreStep = parseInt(this.dataset.loadMoreStep, 10) || 6;
    this.minToShow = parseInt(this.dataset.minToShow, 10) || 1;

    console.log(`${PR_TAG} config →`, {
      apiBase: this.apiBase,
      channel: this.channel,
      productRef: this.productRef,
      initialLimit: this.state.limit,
      loadMoreStep: this.loadMoreStep,
      minToShow: this.minToShow,
    });

    this.refs = {
      summary: this.querySelector('[data-reviews-summary]'),
      average: this.querySelector('[data-reviews-average]'),
      summaryStars: this.querySelector('[data-reviews-stars]'),
      total: this.querySelector('[data-reviews-total]'),
      list: this.querySelector('[data-reviews-list]'),
      loading: this.querySelector('[data-reviews-loading]'),
      empty: this.querySelector('[data-reviews-empty]'),
      error: this.querySelector('[data-reviews-error]'),
      actions: this.querySelector('[data-reviews-actions]'),
      loadMore: this.querySelector('[data-reviews-load-more]'),
      template: this.querySelector('[data-review-template]'),
    };

    const missingRefs = Object.entries(this.refs)
      .filter(([, el]) => !el)
      .map(([k]) => k);

    if (missingRefs.length) {
      console.warn(`${PR_TAG} refs no encontradas en el DOM:`, missingRefs);
    } else {
      console.log(`${PR_TAG} todos los refs del DOM ok`);
    }

    if (!this.apiBase || !this.productRef) {
      console.error(`${PR_TAG} falta apiBase o productRef — abortando`, {
        apiBase: this.apiBase,
        productRef: this.productRef,
      });
      this.showError();
      return;
    }

    this.refs.loadMore?.addEventListener('click', () => this.loadMore());

    this._ro = new ResizeObserver(() => this.layoutMasonry());
    this._ro.observe(this);

    this.initModal();
    this.fetchReviews({ reset: true });
  }

  disconnectedCallback() {
    this._ro?.disconnect();
    this._modal?.remove();
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
  }

  initModal() {
    const m = document.createElement('div');
    m.className = 'pr-modal';
    m.hidden = true;
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.innerHTML = `
      <div class="pr-modal__overlay"></div>
      <div class="pr-modal__dialog">
        <div class="pr-modal__photo-side">
          <img class="pr-modal__img" src="" alt="" loading="eager">
        </div>
        <div class="pr-modal__info-side">
          <button class="pr-modal__close-btn" aria-label="Cerrar">&times;</button>
          <div class="pr-modal__header">
            <span class="pr-modal__author"></span>
            <svg class="pr-modal__check" hidden viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.491 4.491 0 01-3.497-1.307 4.491 4.491 0 01-1.307-3.497A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clip-rule="evenodd"/></svg>
          </div>
          <div class="pr-modal__meta">
            <div class="pr-modal__stars"></div>
            <time class="pr-modal__date"></time>
          </div>
          <p class="pr-modal__body"></p>
        </div>
      </div>
    `;
    this._modal = m;
    document.body.appendChild(m);

    m.querySelector('.pr-modal__overlay').addEventListener('click', () => this.closeModal());
    m.querySelector('.pr-modal__close-btn').addEventListener('click', () => this.closeModal());

    this._onKeydown = (e) => { if (e.key === 'Escape' && !this._modal.hidden) this.closeModal(); };
    document.addEventListener('keydown', this._onKeydown);
  }

  openModal(item) {
    const m = this._modal;
    const photoSide = m.querySelector('.pr-modal__photo-side');
    const dialog = m.querySelector('.pr-modal__dialog');

    if (Array.isArray(item.images) && item.images.length) {
      photoSide.hidden = false;
      m.querySelector('.pr-modal__img').src = item.images[0];
      m.querySelector('.pr-modal__img').alt = item.customerName || '';
      dialog.classList.remove('pr-modal__dialog--no-photo');
    } else {
      photoSide.hidden = true;
      dialog.classList.add('pr-modal__dialog--no-photo');
    }

    m.querySelector('.pr-modal__author').textContent = item.customerName || '—';
    m.querySelector('.pr-modal__check').hidden = !item.isVerifiedPurchase;
    m.querySelector('.pr-modal__stars').innerHTML = this.starsHtml(item.rating);

    const time = m.querySelector('.pr-modal__date');
    if (item.publishedAt) {
      time.dateTime = item.publishedAt;
      time.textContent = new Date(item.publishedAt).toLocaleDateString(
        document.documentElement.lang || 'es-PE',
        { year: 'numeric', month: 'long', day: 'numeric' }
      );
    } else {
      time.textContent = '';
    }

    m.querySelector('.pr-modal__body').textContent = item.body || '';

    m.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this._modal.hidden = true;
    document.body.style.overflow = '';
  }

  getMasonryCols() {
    return window.innerWidth >= 1000 ? 4 : 2;
  }

  getMasonryGap() {
    return window.innerWidth >= 1000 ? 12 : 10;
  }

  layoutMasonry() {
    const list = this.refs.list;
    const items = Array.from(list.querySelectorAll('.product-review'));
    if (!items.length) return;

    const cols = this.getMasonryCols();
    const gap = this.getMasonryGap();
    const totalWidth = list.offsetWidth;
    const colWidth = Math.floor((totalWidth - gap * (cols - 1)) / cols);
    const colHeights = new Array(cols).fill(0);

    items.forEach((item) => {
      item.style.width = colWidth + 'px';
      const col = colHeights.indexOf(Math.min(...colHeights));
      item.style.left = (col * (colWidth + gap)) + 'px';
      item.style.top = colHeights[col] + 'px';
      colHeights[col] += item.offsetHeight + gap;
    });

    list.style.height = Math.max(...colHeights) + 'px';
  }

  waitImagesAndLayout() {
    const imgs = Array.from(this.refs.list.querySelectorAll('img'));
    const pending = imgs.filter((img) => !img.complete);
    if (!pending.length) {
      this.layoutMasonry();
      return;
    }
    let done = 0;
    const tick = () => { done += 1; if (done >= pending.length) this.layoutMasonry(); };
    pending.forEach((img) => {
      img.addEventListener('load', tick, { once: true });
      img.addEventListener('error', tick, { once: true });
    });
  }

  buildUrl({ page, limit }) {
    const params = new URLSearchParams({
      channel: this.channel,
      product_ref: this.productRef,
      page: String(page),
      limit: String(limit),
      sort: 'featured_first',
    });

    if (this.state.filter === 'verified') params.set('verified_only', 'true');
    if (this.state.filter === 'with_images') params.set('with_images', 'true');

    return `${this.apiBase}/api/public/reviews?${params.toString()}`;
  }

  async fetchReviews({ reset = false } = {}) {
    if (this.state.loading) {
      console.log(`${PR_TAG} fetchReviews ignorado — ya hay una petición en curso`);
      return;
    }
    this.state.loading = true;

    if (reset) {
      this.state.page = 1;
      this.state.loaded = 0;
      this.refs.list.innerHTML = '';
      this.refs.empty.hidden = true;
      this.refs.error.hidden = true;
      this.refs.loading.hidden = false;
    }

    const url = this.buildUrl({ page: this.state.page, limit: this.state.limit });
    console.log(`${PR_TAG} fetch →`, url);

    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });

      console.log(`${PR_TAG} response status: ${res.status} ${res.statusText}`);

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      console.log(`${PR_TAG} response data:`, data);

      this.state.total = data.total || 0;
      this.state.pages = data.pages || 1;

      const items = data.items || [];
      console.log(`${PR_TAG} items recibidos: ${items.length} / total: ${this.state.total} / pages: ${this.state.pages}`);

      if (reset) this.allItems = [];
      this.allItems = (this.allItems || []).concat(items);

      this.renderSummary(data.summary, this.allItems);

      items.forEach((item) => this.renderItem(item));
      this.state.loaded += items.length;
      this.state.page += 1;

      this.waitImagesAndLayout();

      this.refs.loading.hidden = true;
      this.refs.actions.hidden = false;

      const hasAny = this.state.total > 0 && this.state.total >= this.minToShow;
      console.log(`${PR_TAG} hasAny: ${hasAny} (total=${this.state.total}, minToShow=${this.minToShow})`);

      if (!hasAny && reset) {
        this.refs.empty.hidden = false;
        this.refs.summary.hidden = true;
        this.refs.loadMore.hidden = true;
        console.log(`${PR_TAG} mostrando estado vacío`);
      } else {
        this.refs.summary.hidden = false;
        const moreByCount = this.state.loaded < this.state.total;
        const moreByPages = (this.state.page - 1) < this.state.pages;
        this.refs.loadMore.hidden = !(moreByCount && moreByPages);
      }

      // After first page, subsequent loads use the step.
      this.state.limit = this.loadMoreStep;
    } catch (err) {
      console.error(`${PR_TAG} fetch failed →`, err);
      this.showError();
    } finally {
      this.state.loading = false;
    }
  }

  loadMore() {
    this.fetchReviews();
  }

  showError() {
    console.error(`${PR_TAG} showError — mostrando mensaje de error al usuario`);
    this.refs.loading.hidden = true;
    this.refs.error.hidden = false;
    this.refs.actions.hidden = true;
  }

  renderSummary(summary, loadedItems) {
    let avg = 0;
    let total = this.state.total;

    if (summary) {
      avg = Number(summary.averageRating || 0);
      total = summary.totalCount != null ? summary.totalCount : total;
    } else if (loadedItems && loadedItems.length) {
      const sum = loadedItems.reduce((acc, it) => acc + (Number(it.rating) || 0), 0);
      avg = sum / loadedItems.length;
    }

    this.refs.average.textContent = avg.toFixed(1);
    this.refs.total.textContent = String(total);
    this.refs.summaryStars.innerHTML = this.starsHtml(avg);
  }

  renderItem(item) {
    console.log(`${PR_TAG} renderItem →`, item);
    const node = this.refs.template.content.cloneNode(true);
    const root = node.querySelector('.product-review');

    // Cover image
    if (Array.isArray(item.images) && item.images.length) {
      const coverWrap = root.querySelector('[data-cover-wrap]');
      const coverImg = root.querySelector('[data-cover]');
      const imgCount = root.querySelector('[data-img-count]');
      coverWrap.hidden = false;
      coverImg.src = item.images[0];
      coverImg.alt = item.customerName || '';
      if (item.images.length > 1) {
        imgCount.textContent = `+${item.images.length - 1}`;
        imgCount.hidden = false;
      }
    }

    root.querySelector('[data-author]').textContent = item.customerName || '—';

    if (item.isVerifiedPurchase) {
      root.querySelector('[data-verified]').hidden = false;
    }

    if (item.publishedAt) {
      const time = root.querySelector('[data-date]');
      const date = new Date(item.publishedAt);
      time.dateTime = item.publishedAt;
      time.textContent = date.toLocaleDateString(document.documentElement.lang || 'es-PE', {
        year: 'numeric', month: 'numeric', day: 'numeric',
      });
    }

    root.querySelector('[data-stars]').innerHTML = this.starsHtml(item.rating);
    root.querySelector('[data-body]').textContent = item.body || '';

    const openFn = () => this.openModal(item);
    const cover = root.querySelector('[data-cover-wrap]');
    if (cover) cover.addEventListener('click', openFn);
    root.querySelector('.product-review__content').addEventListener('click', openFn);

    this.refs.list.appendChild(node);
    console.log(`${PR_TAG} item ${item.id} insertado en el DOM`);
  }

  starsHtml(value) {
    const full = '<svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 1.5l2.6 5.6 6.1.6-4.5 4.2 1.3 6L10 14.9l-5.5 3 1.3-6L1.3 7.7l6.1-.6L10 1.5z"/></svg>';
    const empty = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 1.5l2.6 5.6 6.1.6-4.5 4.2 1.3 6L10 14.9l-5.5 3 1.3-6L1.3 7.7l6.1-.6L10 1.5z"/></svg>';
    const rounded = Math.round(Number(value) || 0);
    let html = '';
    for (let i = 1; i <= 5; i += 1) html += i <= rounded ? full : empty;
    return html;
  }
}

customElements.define('product-reviews', ProductReviews);
console.log(`${PR_TAG} custom element registrado`);
